import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, TrendingUp, Clock, Users, CheckCircle2 } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

// PLACEHOLDER: replace all case studies with real customer stories
const caseStudies = [
  {
    id: 1,
    company: 'Global Financial Services Firm',
    industry: 'Financial Services',
    product: 'AI Voice IT Support',
    challenge: 'A 35,000-employee financial institution was handling 18,000 IT support calls per month, with an average resolution time of 48 hours and a Tier-1 agent cost of £12/ticket.',
    solution: 'Deployed Digian.ai\'s AI Voice IT Support agent integrated with ServiceNow. The agent handles password resets, VPN issues, software requests, and hardware provisioning, escalating to human agents only when needed.',
    results: [
      '72% of Tier-1 tickets resolved automatically',
      'Average resolution time reduced from 48h to under 4 minutes',
      '£1.8M annual cost saving in IT support operations',
      'Employee satisfaction score increased from 3.2 to 4.6 / 5',
    ],
    quote: '',
    quoteName: '',
    featured: true,
  },
  {
    id: 2,
    company: 'European Retail Group',
    industry: 'Retail',
    product: 'AI Customer Care',
    challenge: 'A retail group with 8M+ loyalty customers was struggling with contact centre costs during peak trading periods. Peak season required 3× headcount to maintain SLAs.',
    solution: 'Digian.ai\'s AI Customer Care agent handles order tracking, returns, loyalty queries, and store information, in 14 languages across all digital channels.',
    results: [
      '65% of customer contacts resolved without human intervention',
      'Seasonal headcount requirements reduced by 40%',
      'Customer CSAT maintained at 4.4/5 for AI-handled interactions',
      'Response time reduced from 6h to under 90 seconds',
    ],
    quote: '',
    quoteName: '',
    featured: true,
  },
  {
    id: 3,
    company: 'Healthcare Network',
    industry: 'Healthcare',
    product: 'Analytics Chat Agents',
    challenge: 'A hospital network\'s clinical operations team spent 4+ hours per week waiting for data analysts to generate ad-hoc reports on bed utilisation, staff scheduling, and patient flow.',
    solution: 'Analytics Chat Agents deployed on top of the network\'s governed Snowflake data warehouse. Clinical managers ask questions in plain English and receive instant, governed responses.',
    results: [
      'Report wait time reduced from 4 days to under 30 seconds',
      '80% reduction in ad-hoc BI tickets raised',
      '12 clinical KPIs now self-served by non-technical managers',
      'Data team redirected to strategic analytics work',
    ],
    quote: '',
    quoteName: '',
    featured: false,
  },
  {
    id: 4,
    company: 'Manufacturing Conglomerate',
    industry: 'Manufacturing',
    product: 'Data Prep / Migration',
    challenge: 'A global manufacturer needed to migrate 200+ complex Alteryx workflows to Databricks as part of a cloud data modernisation program, without disrupting weekly reporting cycles.',
    solution: 'Digian.ai\'s Data Prep accelerator auto-translated 78% of workflows, with the solutions team handling the remaining complex blends. Full regression testing was built into the migration pipeline.',
    results: [
      '78% of workflows auto-translated in 3 weeks',
      'Migration completed 4 months ahead of original schedule',
      'Zero production reporting disruptions during cutover',
      '£400K saved versus the original manual migration estimate',
    ],
    quote: '',
    quoteName: '',
    featured: false,
  },
];

const industries = ['All', 'Financial Services', 'Retail', 'Healthcare', 'Manufacturing'];

const productBadgeColors = {
  'AI Voice IT Support': 'bg-indigo-100 text-indigo-700',
  'AI Customer Care': 'bg-violet-100 text-violet-700',
  'Analytics Chat Agents': 'bg-emerald-100 text-emerald-700',
  'Data Prep / Migration': 'bg-amber-100 text-amber-700',
};

const CaseStudiesPage = () => {
  const [activeIndustry, setActiveIndustry] = useState('All');

  const filtered = activeIndustry === 'All'
    ? caseStudies
    : caseStudies.filter(c => c.industry === activeIndustry);

  const featured = filtered.filter(c => c.featured);
  const rest = filtered.filter(c => !c.featured);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="caseStudies" />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">Customer Stories</p>
            <h1 className="text-5xl font-bold text-slate-900">Case Studies</h1>
            <p className="text-lg text-slate-600">
              See how enterprise teams use Digian.ai to reduce costs, accelerate data operations, and deliver better customer experiences.
            </p>
          </motion.div>
        </div>
      </section>


      {/* Filter */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-2 overflow-x-auto py-3">
            {industries.map(ind => (
              <button
                key={ind}
                onClick={() => setActiveIndustry(ind)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeIndustry === ind
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

        {/* Featured case studies */}
        {featured.length > 0 && (
          <div className="mb-16">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Featured Stories</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featured.map((cs, i) => (
                <motion.div key={cs.id} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-slate-50 rounded-2xl border border-slate-200 p-8 hover:border-indigo-300 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${productBadgeColors[cs.product] || 'bg-slate-100 text-slate-600'}`}>
                      {cs.product}
                    </span>
                    <span className="text-xs text-slate-400">{cs.industry}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{cs.company}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{cs.challenge}</p>
                  <div className="space-y-2 mb-5">
                    {cs.results.map((r, ri) => (
                      <div key={ri} className="flex items-start gap-2 text-sm text-slate-700">
                        <TrendingUp className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {r}
                      </div>
                    ))}
                  </div>
                  {/* PLACEHOLDER: link to full case study PDF or dedicated page */}
                  <Link to="/contact">
                    <Button variant="outline" size="sm">Read Full Story <ArrowRight className="ml-1.5 w-3.5 h-3.5" /></Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Remaining case studies */}
        {rest.length > 0 && (
          <div className="mb-16">
            <h2 className="text-xl font-bold text-slate-900 mb-6">More Stories</h2>
            <div className="space-y-6">
              {rest.map((cs, i) => (
                <motion.div key={cs.id} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-sm transition-all">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${productBadgeColors[cs.product] || 'bg-slate-100 text-slate-600'}`}>
                          {cs.product}
                        </span>
                        <span className="text-xs text-slate-400">{cs.industry}</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{cs.company}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-3">{cs.challenge}</p>
                      <div className="grid sm:grid-cols-2 gap-1.5">
                        {cs.results.map((r, ri) => (
                          <div key={ri} className="flex items-start gap-2 text-xs text-slate-700">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            {r}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col justify-center">
                      {/* PLACEHOLDER: link to full case study */}
                      <Link to="/contact">
                        <Button variant="outline" size="sm" className="whitespace-nowrap">Read Story</Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* ROI Calculator CTA */}
        <motion.div {...fadeUp} className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl p-10 text-white text-center">
          <Users className="w-10 h-10 mx-auto mb-4 text-indigo-200" />
          <h3 className="text-2xl font-bold mb-2">Want to see what Digian.ai could do for your organisation?</h3>
          <p className="text-indigo-200 mb-6 max-w-xl mx-auto">
            Our solutions team will walk you through a tailored ROI analysis based on your current volume, team size, and data environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo"><Button size="lg" variant="secondary">Request a Demo</Button></Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                Talk to Sales
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CaseStudiesPage;
